import type { MoveQuality } from 'endchess-contracts';
import {
  MOVE_QUALITY_BUBBLE_STYLES,
  type MoveQualityKey,
} from './moveQualityStyles';

export type MoveQualitySquareStyle = {
  bgcolor: string;
};

export type AnalyzedMoveForReview = {
  plyIndex: number;
  playedUci: string;
  quality: MoveQuality;
};

/** Origin square from a UCI move string. */
export function uciOriginSquare(uci: string): string | null {
  if (uci.length < 4) {
    return null;
  }
  return uci.slice(0, 2);
}

/** Destination square from a UCI move string. */
export function uciDestinationSquare(uci: string): string | null {
  if (uci.length < 4) {
    return null;
  }
  return uci.slice(2, 4);
}

/** Analyzed move for the move that just finished, if any. */
export function findAnalyzedMovePlyForJustPlayed(
  analyzedMoves: readonly AnalyzedMoveForReview[],
  movesPlayedCount: number,
): number | null {
  if (movesPlayedCount <= 0) {
    return null;
  }

  const justPlayedPlyIndex = movesPlayedCount - 1;
  const move = analyzedMoves.find(
    (entry) => entry.plyIndex === justPlayedPlyIndex,
  );
  return move?.plyIndex ?? null;
}

/** @deprecated Use findAnalyzedMovePlyForJustPlayed instead. */
export function findLatestAnalyzedMovePly(
  analyzedMoves: readonly AnalyzedMoveForReview[],
  visibleThroughPly: number,
): number | null {
  return findAnalyzedMovePlyForJustPlayed(analyzedMoves, visibleThroughPly);
}

/** Map a single active move-quality badge to its destination square style. */
export function buildMoveQualitySquareStyles(
  analyzedMoves: readonly AnalyzedMoveForReview[],
  activeBadgePly: number | null,
): Record<string, MoveQualitySquareStyle> {
  if (activeBadgePly === null) {
    return {};
  }

  const move = analyzedMoves.find((entry) => entry.plyIndex === activeBadgePly);
  if (!move) {
    return {};
  }

  const square = uciDestinationSquare(move.playedUci);
  if (!square) {
    return {};
  }

  const quality = move.quality as MoveQualityKey;
  return {
    [square]: {
      bgcolor: MOVE_QUALITY_BUBBLE_STYLES[quality].bgcolor,
    },
  };
}

/** Hide a badge on the origin square while a piece is leaving (e.g. captures). */
export function suppressMoveQualityStylesOnOrigin(
  styles: Record<string, MoveQualitySquareStyle>,
  moveUci: string | null | undefined,
): Record<string, MoveQualitySquareStyle> {
  if (!moveUci) {
    return styles;
  }

  const origin = uciOriginSquare(moveUci);
  if (!origin || !styles[origin]) {
    return styles;
  }

  const next = { ...styles };
  delete next[origin];
  return next;
}
